import { doc, collection, query, where, getDocs, updateDoc } from 'firebase/firestore';
import { db } from './firebase';
import type { UserProfile } from '@monthly-data-collection/shared';

/**
 * Get user profile from system_users collection by firebase_uid
 * This replaces the legacy getUserProfile function that queries the users collection
 */
async function getSystemUserProfile(firebaseUid: string): Promise<UserProfile | null> {
  try {
    console.log(`[AUTH-SYSTEM-USERS] Attempting to get user profile for Firebase UID: ${firebaseUid}`);
    
    // Query system_users collection by firebase_uid field
    const systemUsersRef = collection(db, 'system_users');
    const q = query(systemUsersRef, where('firebase_uid', '==', firebaseUid));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      console.warn(`[AUTH-SYSTEM-USERS] No system_users record found for Firebase UID: ${firebaseUid}`);
      return null;
    }
    
    if (querySnapshot.size > 1) {
      console.error(`[AUTH-SYSTEM-USERS] Multiple system_users records found for Firebase UID: ${firebaseUid}. Using first match.`);
    }
    
    const doc = querySnapshot.docs[0];
    const userData = doc.data();
    
    console.log(`[AUTH-SYSTEM-USERS] Successfully retrieved user profile for Firebase UID: ${firebaseUid}`, {
      userId: doc.id,
      email: userData.email,
      name: userData.employee_data?.name || `${userData.employee_data?.first_name || ''} ${userData.employee_data?.last_name || ''}`.trim(),
      role: userData.role || 'contributor'
    });
    
    // Helper function to convert Firestore Timestamp to Date
    const convertTimestamp = (timestamp: any): Date | undefined => {
      if (!timestamp) return undefined;
      
      // If it's already a Date object, return it
      if (timestamp instanceof Date) return timestamp;
      
      // If it's a Firestore Timestamp, convert it
      if (timestamp && typeof timestamp.toDate === 'function') {
        return timestamp.toDate();
      }
      
      // If it's a timestamp object with seconds/nanoseconds
      if (timestamp && typeof timestamp.seconds === 'number') {
        return new Date(timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000);
      }
      
      // Try to parse as date string
      if (typeof timestamp === 'string') {
        const parsed = new Date(timestamp);
        return isNaN(parsed.getTime()) ? undefined : parsed;
      }
      
      return undefined;
    };

    // Transform system_users data to match UserProfile interface
    const profile: UserProfile = {
      uid: firebaseUid, // Use Firebase UID as the uid
      employee_id: userData.employee_id || userData.id || 0,
      email: userData.email || userData.employee_data?.email || '',
      name: userData.employee_data?.name || `${userData.employee_data?.first_name || ''} ${userData.employee_data?.last_name || ''}`.trim() || 'Unknown User',
      team_name: userData.employee_data?.team_name || userData.employee_data?.department || 'Unknown Team',
      role_name: userData.employee_data?.role_name || userData.employee_data?.position || 'Employee',
      role: userData.role || 'contributor',
      lastActive: convertTimestamp(userData.updated_at) || convertTimestamp(userData.created_at) || new Date(),
      
      // Avatar fields - backward compatible
      avatar: userData.employee_data?.avatar,
      
      // New cached avatar fields
      cached_avatar_url: userData.employee_data?.cached_avatar_url,
      cached_thumbnail_url: userData.employee_data?.cached_thumbnail_url,
      avatar_cache_timestamp: convertTimestamp(userData.employee_data?.avatar_cache_timestamp),
      avatar_cache_status: userData.employee_data?.avatar_cache_status,
      // Group-based access control fields
      groups: userData.groups,
      isSuperAdmin: userData.isSuperAdmin,
    };

    return profile;

  } catch (error) {
    console.error(`[AUTH-SYSTEM-USERS] Error fetching user profile for Firebase UID: ${firebaseUid}`, error);
    throw error;
  }
}

/**
 * Get user profile by email from system_users (fallback when firebase_uid is not populated)
 * This is a temporary helper until all firebase_uid fields are populated
 */
async function getSystemUserProfileByEmail(email: string): Promise<UserProfile | null> {
  try {
    console.log(`[AUTH-SYSTEM-USERS] Attempting to get user profile by email: ${email}`);
    
    // Query system_users collection by email field
    const systemUsersRef = collection(db, 'system_users');
    const q = query(systemUsersRef, where('email', '==', email));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      console.warn(`[AUTH-SYSTEM-USERS] No system_users record found for email: ${email}`);
      return null;
    }
    
    if (querySnapshot.size > 1) {
      console.error(`[AUTH-SYSTEM-USERS] Multiple system_users records found for email: ${email}. Using first match.`);
    }
    
    const doc = querySnapshot.docs[0];
    const userData = doc.data();
    
    console.log(`[AUTH-SYSTEM-USERS] Successfully retrieved user profile by email: ${email}`, {
      userId: doc.id,
      employee_id: userData.employee_id || userData.id,
      name: userData.employee_data?.name || `${userData.employee_data?.first_name || ''} ${userData.employee_data?.last_name || ''}`.trim()
    });
    
    // Transform system_users data to match UserProfile interface
    // Note: uid will be empty since we don't have firebase_uid yet
    const profile: UserProfile = {
      uid: '', // Will be populated when firebase_uid is linked
      employee_id: userData.employee_id || userData.id || 0,
      email: userData.email || userData.employee_data?.email || '',
      name: userData.employee_data?.name || `${userData.employee_data?.first_name || ''} ${userData.employee_data?.last_name || ''}`.trim() || 'Unknown User',
      team_name: userData.employee_data?.team_name || userData.employee_data?.department || 'Unknown Team',
      role_name: userData.employee_data?.role_name || userData.employee_data?.position || 'Employee',
      role: userData.role || 'contributor',
      lastActive: userData.updated_at || userData.created_at || new Date(),
      
      // Avatar fields - backward compatible
      avatar: userData.employee_data?.avatar,
      
      // New cached avatar fields
      cached_avatar_url: userData.employee_data?.cached_avatar_url,
      cached_thumbnail_url: userData.employee_data?.cached_thumbnail_url,
      avatar_cache_timestamp: userData.employee_data?.avatar_cache_timestamp,
      avatar_cache_status: userData.employee_data?.avatar_cache_status,
    };
    
    return profile;
    
  } catch (error) {
    console.error(`[AUTH-SYSTEM-USERS] Error fetching user profile by email: ${email}`, error);
    throw error;
  }
}

/**
 * Resolve a Firebase auth user to their system_users profile.
 *
 * Two strategies, both of which require a pre-existing system_users record:
 *   1. Match on `firebase_uid` field (canonical, populated after first successful login).
 *   2. Match on `email` field, then write the firebase_uid onto the matched doc.
 *
 * Returns null if neither strategy matches. The caller is expected to block the
 * sign-in and surface "contact your administrator" rather than provision a new
 * record on the fly — auto-provisioning would create orphan docs with
 * employee_id=0 that break every downstream employee_id-keyed lookup
 * (FTE attribution, Slack mentions, Asana sync, etc.).
 */
export async function getUserProfileEnhancedFallback(firebaseUid: string, firebaseEmail?: string, _displayName?: string): Promise<UserProfile | null> {
  try {
    const systemUserProfile = await getSystemUserProfile(firebaseUid);
    if (systemUserProfile) {
      console.log(`[AUTH-SYSTEM-USERS] Found user in system_users by firebase_uid: ${firebaseUid}`);
      return systemUserProfile;
    }

    if (firebaseEmail) {
      console.log(`[AUTH-SYSTEM-USERS] Trying system_users by email: ${firebaseEmail}`);
      const emailProfile = await getSystemUserProfileByEmail(firebaseEmail);
      if (emailProfile) {
        emailProfile.uid = firebaseUid;
        console.log(`[AUTH-SYSTEM-USERS] Found user in system_users by email: ${firebaseEmail}`);

        try {
          await associateFirebaseUidWithSystemUser(firebaseEmail, firebaseUid);
          console.log(`[AUTH-SYSTEM-USERS] Successfully auto-associated firebase_uid: ${firebaseUid} with email: ${firebaseEmail}`);
        } catch (associationError) {
          console.error(`[AUTH-SYSTEM-USERS] Failed to auto-associate firebase_uid: ${firebaseUid} with email: ${firebaseEmail}`, associationError);
        }

        return emailProfile;
      }
    }

    console.warn(`[AUTH-SYSTEM-USERS] User not found in any system for Firebase UID: ${firebaseUid}, email: ${firebaseEmail}`);
    return null;

  } catch (error) {
    console.error(`[AUTH-SYSTEM-USERS] Error in getUserProfileEnhancedFallback for Firebase UID: ${firebaseUid}`, error);
    throw error;
  }
}

/**
 * Associate a Firebase UID with a system_users record by email
 * This function automatically updates the system_users record when a user is found by email
 */
async function associateFirebaseUidWithSystemUser(email: string, firebaseUid: string): Promise<boolean> {
  try {
    console.log(`[AUTH-SYSTEM-USERS] Auto-associating Firebase UID for email: ${email}`);
    
    // Find the system_users record by email
    const systemUsersRef = collection(db, 'system_users');
    const q = query(systemUsersRef, where('email', '==', email));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      console.log(`[AUTH-SYSTEM-USERS] No system_users record found for email: ${email}`);
      return false;
    }
    
    const systemUserDoc = querySnapshot.docs[0];
    const systemUserData = systemUserDoc.data();
    
    // Check if firebase_uid is already set
    if (systemUserData.firebase_uid && systemUserData.firebase_uid !== firebaseUid) {
      console.log(`[AUTH-SYSTEM-USERS] Firebase UID already set to different value: ${systemUserData.firebase_uid}`);
      return false;
    }
    
    if (systemUserData.firebase_uid === firebaseUid) {
      console.log(`[AUTH-SYSTEM-USERS] Firebase UID already correctly set: ${firebaseUid}`);
      return true;
    }
    
    // Update the system_users record with firebase_uid
    const docRef = doc(db, 'system_users', systemUserDoc.id);
    await updateDoc(docRef, {
      firebase_uid: firebaseUid,
      association_status: 'automatic',
      updated_at: new Date()
    });
    
    console.log(`[AUTH-SYSTEM-USERS] Successfully auto-associated Firebase UID: ${firebaseUid} with email: ${email}`);
    return true;
    
  } catch (error) {
    console.error(`[AUTH-SYSTEM-USERS] Error auto-associating Firebase UID for email ${email}:`, error);
    return false;
  }
}